import { App } from "@app/interfaces"

export class KeycloakTokenService {
  private tokenEndpoint: string | null = null
  private clientId: string | null = null

  private ensureConfig(): void {
    if (this.tokenEndpoint && this.clientId) return

    const keycloakUrl = import.meta.env.REACT_APP_KEYCLOAK_URL
    const realm = import.meta.env.REACT_APP_KEYCLOAK_REALM
    this.clientId = import.meta.env.REACT_APP_KEYCLOAK_CLIENT_ID

    if (!keycloakUrl || !realm || !this.clientId) {
      throw new Error(
        "Missing required Keycloak configuration for token service",
      )
    }

    this.tokenEndpoint = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`
  }

  isConfigured(): boolean {
    try {
      const keycloakUrl = import.meta.env.REACT_APP_KEYCLOAK_URL
      const realm = import.meta.env.REACT_APP_KEYCLOAK_REALM
      const clientId = import.meta.env.REACT_APP_KEYCLOAK_CLIENT_ID
      return !!(keycloakUrl && realm && clientId)
    } catch {
      return false
    }
  }

  /**
   * Обновление access token через refresh token
   */
  async refreshToken(refreshToken: string): Promise<App.OIDCAuthResult> {
    this.ensureConfig()
    try {
      const formData = new URLSearchParams()
      formData.append("grant_type", "refresh_token")
      formData.append("client_id", this.clientId!)
      formData.append("refresh_token", refreshToken)

      const response = await fetch(this.tokenEndpoint!, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Token refresh failed: ${response.status} ${errorText}`)
      }

      const tokenData = await response.json()

      return {
        access_token: tokenData.access_token,
        id_token: tokenData.id_token,
        refresh_token: tokenData.refresh_token || refreshToken, // Keycloak может вернуть новый refresh token
        token_type: tokenData.token_type || "Bearer",
        expires_in: tokenData.expires_in,
        scope: tokenData.scope,
      }
    } catch (error) {
      console.error("Direct token refresh failed:", error)
      throw error
    }
  }

  /**
   * Проверяет, нужно ли обновить токен (за 2 минуты до истечения)
   */
  shouldRefreshToken(expiresAt?: number): boolean {
    if (!expiresAt) return false

    const now = Math.floor(Date.now() / 1000)
    const timeUntilExpiry = expiresAt - now

    // Обновляем токен за 120 секунд до истечения
    return timeUntilExpiry <= 120
  }

  /**
   * Получение информации о пользователе из ID токена
   */
  parseUserInfo(idToken: string): Partial<App.OIDCUserInfo> {
    try {
      const payload = JSON.parse(atob(idToken.split(".")[1]))

      return {
        sub: payload.sub,
        preferred_username: payload.preferred_username,
        email: payload.email,
        given_name: payload.given_name,
        family_name: payload.family_name,
        roles: payload.realm_access?.roles || [],
        realm_access: payload.realm_access,
        resource_access: payload.resource_access,
      }
    } catch (error) {
      console.error("Failed to parse user info from ID token:", error)
      return {}
    }
  }
}

export const keycloakTokenService = new KeycloakTokenService()
